import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Lightbulb, Rocket, CheckCircle, ArrowRight } from "lucide-react";
import CompanyImage from "../../assets/images/econsign-aboutus2.jpeg";
import "./AboutUs.scss";

// Animation Variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2 }
  }
};

export default function AboutUs() {
  return (
    <div className="about-page">
      {/* --- Hero Section --- */}
      <section className="about-hero-section">
        <div className="overlay"></div>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1 }}
          className="hero-content"
        >
          <span className="badge">Innovation at Core</span>
          <h1>Designing the Future of <br /><span>Digital Excellence</span></h1>
          <p>
            EconSign Soft delivers powerful, scalable, and modern digital 
            solutions tailored for the next generation of businesses.
          </p>
          
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Link to="/contact-us" className="about-btn-primary">
              Learn More <ArrowRight size={18} />
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* --- Company Section --- */}
      <section className="company-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="company-image-wrapper"
          >
            <img src={CompanyImage} alt="Our Team Workspace" />
            <div className="experience-badge">
              <strong>13+</strong>
              <span>Years of Innovation</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="company-text"
          >
            <span className="label">Who We Are</span>
            <h2>Empowering Businesses Through <span>Tailored Software</span></h2>
            <p>
              EconSign Soft is a renowned software development company dedicated
              to delivering end-to-end IT solutions globally. We combine innovation, 
              quality, and reliability to address unique digital challenges.
            </p>
            <ul className="feature-list">
              <li><CheckCircle size={20} /> Next-gen Technology Stack</li>
              <li><CheckCircle size={20} /> Human-First Design Approach</li>
              <li><CheckCircle size={20} /> Scalable & Secure Architecture</li>
            </ul>
          </motion.div>
        </div>
      </section>

      {/* --- Process Section --- */}
      <section className="process-section">
        <div className="container">
          <div className="section-header">
            <span className="label">Our Workflow</span>
            <h2 className="section-title">How We Bring Ideas To Life</h2>
          </div>

          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="process-grid"
          >
            {[
              { icon: <Lightbulb />, title: "Discovery", desc: "Deep discovery sessions to understand your goals & target audience." },
              { icon: <Users />, title: "Prototyping", desc: "UI/UX experts crafting wireframes and interactive flow diagrams." },
              { icon: <Rocket />, title: "Execution", desc: "Clean code and scalable architecture brought to life by experts." },
              { icon: <CheckCircle />, title: "Deployment", desc: "Quality checks, optimization, and seamless global launch." }
            ].map((step, index) => (
              <motion.div key={index} variants={fadeInUp} className="process-card">
                <div className="icon-box">{step.icon}</div>
                <span className="step-number">0{index + 1}</span>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* --- CTA Section --- */}
      <section className="cta-section">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="cta-container"
        >
          <div className="cta-info">
            <h2>Ready to transform your business?</h2>
            <p>Book a free consultation call with our technical experts today.</p>
          </div>
          <div className="cta-form-wrapper">
            <form className="modern-form" onSubmit={(e) => e.preventDefault()}>
              <div className="input-group">
                <input type="text" placeholder="Full Name" required />
                <input type="email" placeholder="Email Address" required />
              </div>
              <input type="tel" placeholder="Phone Number" />
              <textarea placeholder="Tell us about your project..." />
              <button type="submit" className="btn-submit">Send Message</button>
            </form>
          </div>
        </motion.div>
      </section>
    </div>
  );
}